import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import LOGO from "assets/logo.svg";
import {
  AlertCircle,
  Clock,
  CreditCard,
  ExternalLink,
  Info,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import {
  useCancelCharge,
  useCreateCharge,
  useGetChargeInfo,
} from "hooks/usePaymentCharge";

import PaymentMethodSelector from "js/components/Payments/PaymentMethodSelector";
import PaynetReceipt from "js/components/Payments/PaynetReceipt";
import SpeiPaymentSlip from "js/components/Payments/SpeiPaymentSlip";
import { Button } from "js/components/ui/button";
import { Card, CardContent } from "js/components/ui/card";

import { CreateChargeResponse, PaymentData } from "./types";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(amount);

const PaymentLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-muted/40 p-4">
    <img src={LOGO} alt="Multiburo" className="mb-8 h-16 w-auto" />
    <div className="w-full max-w-2xl">{children}</div>
    <p className="mt-8 text-xs text-muted-foreground">
      Pagos procesados de forma segura por OpenPay
    </p>
  </div>
);

const PaymentPage: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get("status");

  const [pollInterval, setPollInterval] = useState<number | false>(false);
  const [errorDismissed, setErrorDismissed] = useState(false);

  const {
    data: chargeInfo,
    isLoading,
    isError,
  } = useGetChargeInfo(type || "", id || "", true, pollInterval);

  const createChargeMutation = useCreateCharge();
  const cancelChargeMutation = useCancelCharge();

  const hasActiveCardCharge =
    chargeInfo?.is_payment_in_progress &&
    chargeInfo?.payment_method === "card" &&
    !!chargeInfo?.payment_data?.redirect_url;

  const needsRedirectUrl =
    chargeInfo?.payment_completed &&
    !chargeInfo?.is_payment_in_progress &&
    !chargeInfo?.redirect_url;

  useEffect(() => {
    if (chargeInfo?.payment_completed && chargeInfo?.is_payment_in_progress) {
      setPollInterval(12000);
    } else if (hasActiveCardCharge) {
      // Poll while user is on OpenPay's card capture page (opened in new tab)
      setPollInterval(12000);
    } else if (needsRedirectUrl) {
      // Poll until multiexpediente_url is ready
      setPollInterval(12000);
    } else if (
      paymentStatus === "success" &&
      chargeInfo &&
      !chargeInfo.payment_completed
    ) {
      // Poll while waiting for backend to confirm a payment the gateway reported as successful
      setPollInterval(5000);
    } else {
      setPollInterval(false);
    }
  }, [
    chargeInfo?.payment_completed,
    chargeInfo?.is_payment_in_progress,
    hasActiveCardCharge,
    needsRedirectUrl,
    paymentStatus,
  ]);

  useEffect(() => {
    if (
      chargeInfo?.payment_completed &&
      !chargeInfo?.is_payment_in_progress &&
      chargeInfo?.redirect_url
    ) {
      const timer = setTimeout(() => {
        window.location.href = chargeInfo.redirect_url;
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [
    chargeInfo?.payment_completed,
    chargeInfo?.is_payment_in_progress,
    chargeInfo?.redirect_url,
  ]);

  const handleCancelCharge = () => {
    if (!type || !id) return;
    cancelChargeMutation.mutate(
      { resource_type: type, resource_id: id },
      { onSuccess: () => createChargeMutation.reset() },
    );
  };

  const handleMethodSelect = async (
    method: "card" | "store" | "bank_account",
  ) => {
    if (!type || !id) return;

    createChargeMutation.mutate({
      resource_type: type,
      resource_id: id,
      method,
    });
  };

  const handleRetryCard = () => {
    if (!type || !id) return;
    setErrorDismissed(true);
    cancelChargeMutation.mutate(
      { resource_type: type, resource_id: id },
      {
        onSuccess: () => {
          createChargeMutation.mutate({
            resource_type: type,
            resource_id: id,
            method: "card",
          });
        },
      },
    );
  };

  const handleChangeMethodFromError = () => {
    if (!type || !id) return;
    setErrorDismissed(true);
    handleCancelCharge();
  };

  const isRetryingCard =
    cancelChargeMutation.isPending || createChargeMutation.isPending;

  // State: Loading
  if (isLoading) {
    return (
      <PaymentLayout>
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PaymentLayout>
    );
  }

  // State: Error (only on initial load — if we already have data, keep showing it)
  if (!chargeInfo) {
    return (
      <PaymentLayout>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {isError ? "No pudimos cargar el pago" : "Enlace no válido"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isError
                ? "Ocurrió un problema al consultar el pago. Intenta nuevamente."
                : "El enlace de pago no es válido o ha expirado."}
            </p>
          </CardContent>
        </Card>
      </PaymentLayout>
    );
  }

  // State: Card payment failed (redirected back from OpenPay)
  if (
    !errorDismissed &&
    (paymentStatus === "error" || paymentStatus === "failed")
  ) {
    return (
      <PaymentLayout>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              No fue posible realizar el pago
            </h2>
            <p className="text-sm text-muted-foreground">
              El pago con tarjeta no pudo ser procesado. Puedes intentarlo de
              nuevo o elegir otro método de pago.
            </p>
            <div className="flex w-full flex-col gap-2 pt-2">
              <Button
                className="w-full"
                disabled={isRetryingCard}
                onClick={handleRetryCard}
              >
                {isRetryingCard ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Reintentar con tarjeta
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                disabled={isRetryingCard}
                onClick={handleChangeMethodFromError}
              >
                Elegir otro método de pago
              </Button>
            </div>
          </CardContent>
        </Card>
      </PaymentLayout>
    );
  }

  // State: URL says payment succeeded, waiting for backend confirmation
  if (paymentStatus === "success" && !chargeInfo.payment_completed) {
    return (
      <PaymentLayout>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Procesando tu pago
            </h2>
            <p className="text-sm text-muted-foreground">
              Tu pago está siendo verificado. Esta página se actualizará
              automáticamente.
            </p>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </PaymentLayout>
    );
  }

  // State: Payment fully completed (not in progress anymore)
  if (chargeInfo.payment_completed && !chargeInfo.is_payment_in_progress) {
    return (
      <PaymentLayout>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <ShieldCheck className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Pago completado
            </h2>
            <p className="text-sm text-muted-foreground">
              {chargeInfo.redirect_url
                ? "Redirigiendo a tu reporte..."
                : "Estamos preparando tu reporte, serás redirigido en un momento."}
            </p>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </PaymentLayout>
    );
  }

  // State: Payment received, waiting for finalization (polling active)
  if (chargeInfo.payment_completed) {
    return (
      <PaymentLayout>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Pago recibido
            </h2>
            <p className="text-sm text-muted-foreground">
              Tu pago ha sido recibido y está siendo procesado.
            </p>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </PaymentLayout>
    );
  }

  // State: Not in progress
  if (!chargeInfo.is_payment_in_progress) {
    return (
      <PaymentLayout>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <Info className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Pago no disponible
            </h2>
            <p className="text-sm text-muted-foreground">
              Este recurso no se encuentra en proceso de pago.
            </p>
          </CardContent>
        </Card>
      </PaymentLayout>
    );
  }

  // State: Active payment — show method selector or receipt
  const existingMethod = chargeInfo.payment_method;
  const existingData = chargeInfo.payment_data;
  const mutationData = createChargeMutation.data as
    | CreateChargeResponse
    | undefined;

  const hasExistingCharge =
    existingMethod && Object.keys(existingData || {}).length > 0;
  const hasMutationResult = !!mutationData;

  const activeMethod = hasMutationResult ? mutationData.method : existingMethod;
  const activeData = hasMutationResult ? mutationData : existingData;

  const hasCharge = hasExistingCharge || hasMutationResult;
  const showReceipt = hasCharge && activeMethod !== "card";

  // Resolve the redirect URL for existing card charges
  const cardRedirectUrl = activeData?.redirect_url;
  const showCardPending =
    hasCharge && activeMethod === "card" && cardRedirectUrl;

  return (
    <PaymentLayout>
      <div className="mb-6 text-center">
        <h2 className="text-lg font-semibold text-foreground">Pago</h2>
        <p className="text-sm text-muted-foreground">
          {chargeInfo.description}
        </p>
        <p className="mt-2 text-3xl font-bold text-foreground">
          {formatCurrency(chargeInfo.amount)}
        </p>
      </div>

      {showCardPending ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <CreditCard className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Pago con tarjeta pendiente
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Tienes un pago con tarjeta en proceso. Se abrirá la página de
                pago en una nueva pestaña.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 pt-2">
              <Button
                className="w-full"
                onClick={() => {
                  window.open(cardRedirectUrl, "_blank");
                }}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Continuar al pago con tarjeta
                <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={handleCancelCharge}
                disabled={cancelChargeMutation.isPending}
              >
                {cancelChargeMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Cambiar método de pago
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : showReceipt && activeMethod === "store" && activeData ? (
        <PaynetReceipt
          paymentData={activeData as PaymentData}
          amount={chargeInfo.amount}
          onCancel={handleCancelCharge}
          cancelLoading={cancelChargeMutation.isPending}
        />
      ) : showReceipt && activeMethod === "bank_account" && activeData ? (
        <SpeiPaymentSlip
          paymentData={activeData as PaymentData}
          amount={chargeInfo.amount}
          onCancel={handleCancelCharge}
          cancelLoading={cancelChargeMutation.isPending}
        />
      ) : (
        <PaymentMethodSelector
          onSelect={handleMethodSelect}
          loading={createChargeMutation.isPending}
        />
      )}
    </PaymentLayout>
  );
};

export default PaymentPage;
